import { Component, ViewEncapsulation, Input, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Events } from 'src/providers/events/events';
import { ModalController } from '@ionic/angular';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Util } from 'src/shared/util/util';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { BillingHistoryPage } from 'src/pages/billing-history/billing-history';
import { JobPage } from 'src/pages/schedule/job/job';
import { AddProsOnboardingComponent } from 'src/shared/components/onboarding/add-pros/add-pros.component';
import { SmartNumberOnboardingComponent } from 'src/shared/components/onboarding/smart-number-onboarding/smart-number-onboarding.component';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { SmartlockTestCodeComponent } from 'src/shared/components/smartlock-test-code/smartlock-test-code.component';

@Component({
    selector: 'success-page',
    styleUrls: ['./success.scss'],
    templateUrl: './success.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SuccessPage implements OnDestroy, OnInit {

  params: SuccessPageParamsModel;
  @Input() modalParams: SuccessPageParamsModel;
  errorMessage: string;
  modalSubscription: Subscription;
  dialogParams: any;
  isRightSideContent = true;

  
  boundSecondButtonAction = this.secondButtonAction.bind(this);
  boundButtonAction = this.buttonAction.bind(this);

constructor(
    private customNavCtrl: CustomNavController,
    private util: Util,
    private events: Events,
    private modalCtrl: ModalController,
    private router: Router,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.params = this.dialogParams || this.customNavCtrl.getParams() || this.modalParams || this.params;
    if (this.params?.showDesktopNav === false) {
      this.events.publish('menu:close');
    }
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.modalSubscription = this.modalParams?.dismiss?.subscribe(dissmiss => {
      if (dissmiss) {
        this.dismissModal(this.modalParams?.buttonRoute)
      }
    });
  }

  async buttonAction() {
    try {
      if (this.params?.customActionKey === 'goToTIDYForPros') {
        this.util.openUrl('https://pro.tidy.com/#/login');
        return this.customNavCtrl.navigateForward('login');
      }
      if (this.params?.customActionKey === 'closeRightSidePanel') {
        return this.rightSidePanelService.closeRightSidePanel();
      }
      if (this.params?.customActionKey === 'navigateToBillingHistory') {
        return await this.rightSidePanelService.navigateTo('billing-history', {}, BillingHistoryPage);
      }
      if (this.params?.customActionKey === 'navigateToJobPage') {
        return await this.rightSidePanelService.navigateTo('job', { bookingId: this.params['bookingId'] }, JobPage);
      }
      if (this.params?.customActionKey === 'navigateToAddPros') {
        this.rightSidePanelService.setDialogPageTitle('Add Your Pros');
        return await this.rightSidePanelService.navigateTo(
          'onboarding/addPros',
          { addressId: this.params['addressId'], flow: 'addPros' },
          AddProsOnboardingComponent
        );
      }
      if (this.params?.customActionKey === 'navigateToSmartNumber') {
        this.rightSidePanelService.setDialogPageTitle('Smart Number');
        return await this.rightSidePanelService.navigateTo(
          'getting-started/smart-number',
          { addressId: this.params['addressId'] },
          SmartNumberOnboardingComponent
        );
      }
      if (this.params?.customActionKey === 'navigateToConnectListings') {
        this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
        return await this.rightSidePanelService.navigateTo(
          'listings-setup',
          { addressId: this.params['addressId'] },
          ListingsSetupComponent
        );
      }
      if (this.params?.customActionKey === 'navigateToSmartlockTestCode') {
        this.rightSidePanelService.setDialogPageTitle('Generate Access Code');
        return await this.rightSidePanelService.navigateTo(
          'smartlock-test-code',
          { preselectedDeviceId: this.params['justConnectedDeviceId'] },
          SmartlockTestCodeComponent
        );
      }
      if (this.params?.customAction) {
        await this.params.customAction();
        this.params.actionSubject?.next();
        return;
      }
      if (!this.params.externalUrl) {
        this.params.actionSubject?.next();
        this.customNavCtrl.navigateRoot(this.params.buttonRoute || 'properties', this.params.navigationExtras || {});
      } else {
        this.util.openUrl(this.params.buttonRoute);
      }
      this.dismissModal(this.modalParams?.buttonRoute);
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  dismissDialog(): void {
    this.rightSidePanelService.closeRightSidePanel();
  }

  async secondButtonAction() {
    try {
      if (this.params?.secondCustomActionKey) {
        const savedKey = this.params.customActionKey;
        this.params.customActionKey = this.params.secondCustomActionKey;
        await this.buttonAction();
        this.params.customActionKey = savedKey;
        return;
      }
      await this.params.secondCustomAction();
      this.dismissModal();
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async dismissModal(targetPage: string = null) {
    const topModal = await this.modalCtrl.getTop();
    if (topModal) {
      await this.modalCtrl.dismiss(topModal.id);
    }

    if (this.router.url.includes(targetPage)) {
      window.location.reload();
    }
  }

  ngOnDestroy() {
    this.modalSubscription?.unsubscribe();
  }
}

export interface SuccessPageParamsModel {
  header: string;
  externalUrl?: boolean;
  body: string;
  buttonText: string;
  buttonRoute?: string;
  titlecase?: boolean;
  navigationExtras?: {[key: string]: string}
  customAction?: (data?) => Promise<any>;
  customActionKey?: string;
  showDesktopNav?: boolean;
  secondButtonText?: string;
  secondCustomAction?: (data?) => Promise<any>;
  secondCustomActionKey?: string;
  allButtonsPrimary?: boolean;
  dismiss?: EventEmitter<boolean>;
  actionSubject?: Subject<void>;
  justConnectedDeviceId?: number | null;
}
